import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  profile,
  journal,
  paper,
  sideProjects,
  selectedWork,
  protocols,
  languages,
  standards,
} from "../data";

const ORIGIN = "https://www.theaibunny.com";
const PERSON_ID = `${ORIGIN}/#person`;
const SITE_ID = `${ORIGIN}/#website`;
const SAME_AS = [
  "https://github.com/aibunny",
  "https://x.com/TheAIbunny",
  "https://twitter.com/TheAIbunny",
  "https://www.linkedin.com/in/waihenya-/",
  "https://www.sereel.com/",
];

const MONTHS = { Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06", Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12" };
export function isoDate(text) {
  const m = /^([A-Z][a-z]{2})(?: (\d{1,2}),)? (\d{4})$/.exec((text || "").trim());
  return m ? `${m[3]}-${MONTHS[m[1]]}-${String(m[2] || 1).padStart(2, "0")}` : undefined;
}

const knowsAbout = [
  "Institutional tokenization",
  "Security tokens",
  "ERC-3643 (T-REX)",
  "CMTAT",
  "OnchainID",
  "ERC-4626 vaults",
  "Institutional custody",
  "Multisig wallets",
  "Key management",
  "Transaction orchestration",
  "Private settlement",
  "Institutional DeFi",
  "Canton Network",
  "DAML",
  "ZKsync Prividium",
  "Validator node operations",
  "Agentic wallets",
  "AI agent orchestration",
  ...profile.networks.map((n) => n.name),
  ...protocols.map((x) => x.name),
  ...languages,
  ...standards,
  "Kubernetes",
  "PostgreSQL",
  "Kafka",
];

const person = {
  "@type": "Person",
  "@id": PERSON_ID,
  name: "Bunny",
  alternateName: ["TheAIbunny", "0xbunny", "AI Bunny"],
  jobTitle: "Co-Founder & CTO, Sereel; Blockchain Infrastructure Engineer",
  description:
    "Blockchain infrastructure engineer who designs custody, tokenization, and settlement infrastructure for institutions moving real assets onchain.",
  url: `${ORIGIN}/`,
  image: `${ORIGIN}/og-institutional.png`,
  email: `mailto:${profile.email}`,
  nationality: { "@type": "Country", name: "Kenya" },
  sameAs: SAME_AS,
  worksFor: { "@type": "Organization", name: "Sereel", url: "https://www.sereel.com/" },
  hasOccupation: {
    "@type": "Occupation",
    name: "Blockchain Infrastructure Engineer",
    occupationalCategory: "15-1252.00",
    skills: knowsAbout.join(", "),
  },
  knowsAbout,
  knowsLanguage: ["en", "sw"],
};

const website = {
  "@type": "WebSite",
  "@id": SITE_ID,
  url: `${ORIGIN}/`,
  name: "Bunny",
  alternateName: "TheAIbunny",
  description: "Portfolio of Bunny, blockchain infrastructure engineer: institutional tokenization, custody, and settlement.",
  publisher: { "@id": PERSON_ID },
  inLanguage: "en",
};

function homeGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      website,
      person,
      {
        "@type": "ProfilePage",
        "@id": `${ORIGIN}/#profile`,
        url: `${ORIGIN}/`,
        name: "Bunny, blockchain infrastructure engineer",
        isPartOf: { "@id": SITE_ID },
        mainEntity: { "@id": PERSON_ID },
        about: { "@id": PERSON_ID },
        dateModified: new Date().toISOString().slice(0, 10),
      },
      {
        "@type": "ScholarlyArticle",
        "@id": `${ORIGIN}${paper.pdf}#paper`,
        headline: paper.title,
        name: paper.title,
        url: `${ORIGIN}${paper.pdf}`,
        sameAs: paper.source,
        encodingFormat: "application/pdf",
        datePublished: "2025-09-09",
        inLanguage: "en",
        author: [{ "@type": "Person", name: "Lance Davis" }, { "@id": PERSON_ID }],
        about: ["Institutional DeFi", "Tokenization", "Emerging markets", "Compliance"],
      },
      ...sideProjects.map((s) => ({
        "@type": "SoftwareSourceCode",
        name: s.title,
        description: s.text,
        url: s.url,
        codeRepository: s.repository || (s.url.includes("github.com") ? s.url : undefined),
        programmingLanguage: s.programmingLanguage,
        author: { "@id": PERSON_ID },
      })),
      {
        "@type": "ItemList",
        name: "Selected work",
        itemListElement: selectedWork.map((w, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: w.name,
          description: w.subhead,
          url: `${ORIGIN}/#work-${w.id}`,
        })),
      },
      {
        "@type": "Blog",
        "@id": `${ORIGIN}/#journal`,
        url: `${ORIGIN}/#journal`,
        name: "Notes by Bunny",
        author: { "@id": PERSON_ID },
        blogPost: journal.map((j) => ({
          "@type": "BlogPosting",
          headline: j.title,
          url: `${ORIGIN}/journal/${j.slug}`,
          datePublished: isoDate(j.date),
          author: { "@id": PERSON_ID },
        })),
      },
    ],
  };
}

function articleGraph(article, url, description, img) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        headline: article.title,
        description,
        image: img,
        url,
        datePublished: isoDate(article.date),
        dateModified: isoDate(article.date),
        inLanguage: "en",
        isPartOf: { "@id": `${ORIGIN}/#journal` },
        author: { "@id": PERSON_ID },
        publisher: { "@id": PERSON_ID },
        ...(article.xUrl && { sameAs: [article.xUrl] }),
      },
      person,
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Bunny", item: `${ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "Notes", item: `${ORIGIN}/#journal` },
          { "@type": "ListItem", position: 3, name: article.title, item: url },
        ],
      },
    ],
  };
}

const Seo = ({ title, description, image, article, preloadHero }) => {
  const { pathname } = useLocation();
  const url = `${ORIGIN}${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
  const img = image ? new URL(image, ORIGIN).href : `${ORIGIN}/og-institutional.png`;
  const isHomepage = pathname === "/";
  const graph = isHomepage ? homeGraph() : article ? articleGraph(article, url, description, img) : null;
  return (
    <Helmet>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="Bunny" />
      <link rel="canonical" href={url} />
      {preloadHero && (
        <link rel="preload" as="image" href="/images/bunny-financial-world.jpg" media="(min-width: 768px)" fetchpriority="high" />
      )}
      {preloadHero && (
        <link rel="preload" as="image" href="/images/bunny-financial-world-1000.jpg" media="(max-width: 767px)" fetchpriority="high" />
      )}
      <meta property="og:type" content={article ? "article" : "profile"} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Bunny" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={article ? article.title : "Bunny, blockchain infrastructure engineer"} />
      {article && <meta property="article:published_time" content={isoDate(article.date)} />}
      {article && <meta property="article:author" content={`${ORIGIN}/`} />}
      {article && <meta property="article:section" content="Blockchain infrastructure" />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@TheAIbunny" />
      <meta name="twitter:creator" content="@TheAIbunny" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {graph && <script type="application/ld+json">{JSON.stringify(graph)}</script>}
    </Helmet>
  );
};

export default Seo;
